import React from 'react';
import { defineMessages, injectIntl } from 'react-intl';
import moment from 'moment';
import Highlight from 'react-highlighter';

import TextOverflow from '../../../components/TextOverflow';

const messages = defineMessages({
  NO_USERS_FOUND: {
    id: 'AllUsers.NO_USERS_FOUND',
    defaultMessage: 'Es wurden keine Benutzer gefunden.',
  },
  NAME: {
    id: 'AllUsers.NAME',
    defaultMessage: 'Name',
  },
  EMAIL: {
    id: 'AllUsers.EMAIL',
    defaultMessage: 'E-Mail-Adresse',
  },
  PHONE: {
    id: 'AllUsers.PHONE',
    defaultMessage: 'Telefonnummer',
  },
  CREATED: {
    id: 'AllUsers.CREATED',
    defaultMessage: 'Hinzugefügt am',
  },
  LAST_LOGIN: {
    id: 'AllUsers.LAST_LOGIN',
    defaultMessage: 'Letzte Anmeldung',
  },
  ACTIVITY: {
    id: 'AllUsers.ACTIVITY',
    defaultMessage: 'Aktivität',
  },
  COUNT_COMPANIES: {
    id: 'AllUsers.COUNT_COMPANIES',
    defaultMessage: 'Anzahl der Firmen',
  },
  CUSTOMIZE_COLUMNS: {
    id: 'AllUsers.CUSTOMIZE_COLUMNS',
    defaultMessage: 'Spalten anpassen',
  },
  OWNER: {
    id: 'AllUsers.OWNER',
    defaultMessage: 'Besitzer',
  },
  ADMIN: {
    id: 'AllUsers.ADMIN',
    defaultMessage: 'Administrator',
  },
  DISABLED: {
    id: 'AllUsers.DISABLED',
    defaultMessage: 'Deaktiviert',
  },
  DELETED: {
    id: 'AllUsers.DELETED',
    defaultMessage: 'Gelöscht',
  },
  ROLE: {
    id: 'AllUsers.ROLE',
    defaultMessage: 'Rolle',
  },
  USER: {
    id: 'AllUsers.USER',
    defaultMessage: 'Benutzer',
  },
  COMPANY: {
    id: 'AllUsers.COMPANY',
    defaultMessage: 'Unternehmen',
  },
  EXPORT_CSV: {
    id: 'AllUsers.EXPORT_CSV',
    defaultMessage: 'Auswahl als CSV exportieren',
  },
});

class AllUsers extends React.PureComponent {
  constructor(props) {
    super(props);
    const savedState = localStorage.getItem('AllUsersTableState_v6');
    const defaultState = {
      sortColumn: 'created',
      sortDirection: 'desc',
      selectedUsers: [],
      showColumnPicker: false,
      visibleColumns: ['colUser', 'col2', 'colCompany', 'colActivity', 'colRole', 'col5'],
      columnWidths: {
        colSelect: 50, // selection checkbox
        colUser: 320, // name & email
        col2: 150, // phone
        colCompany: 220, // company
        colActivity: 180, // created & last login
        colRole: 120, // role
        col5: 120, // companies count
      }
    };
    
    this.state = savedState ? { ...defaultState, ...JSON.parse(savedState), selectedUsers: [], showColumnPicker: false } : defaultState;
    this.resizingColumn = null;
    this.startX = 0;
    this.startWidth = 0;
  }

  componentWillUnmount() {
    document.removeEventListener('mousemove', this.handleResizeMove);
    document.removeEventListener('mouseup', this.handleResizeEnd);
  }

  saveState = () => {
    const { sortColumn, sortDirection, columnWidths, visibleColumns } = this.state;
    localStorage.setItem('AllUsersTableState_v6', JSON.stringify({ sortColumn, sortDirection, columnWidths, visibleColumns }));
  }

  toggleSort = (column) => {
    this.setState(prevState => {
      let nextDir = 'asc';
      if (prevState.sortColumn === column) {
        nextDir = prevState.sortDirection === 'asc' ? 'desc' : 'asc';
      }
      return {
        sortColumn: column,
        sortDirection: nextDir
      };
    }, this.saveState);
  }

  handleResizeStart = (e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();
    this.resizingColumn = columnKey;
    this.startX = e.pageX;
    this.startWidth = this.state.columnWidths[columnKey];
    
    document.addEventListener('mousemove', this.handleResizeMove);
    document.addEventListener('mouseup', this.handleResizeEnd);
  };

  handleResizeMove = (e) => {
    if (!this.resizingColumn) return;
    const diff = e.pageX - this.startX;
    const newWidth = Math.max(50, this.startWidth + diff); // minimum width 50px
    this.setState(prevState => ({
      columnWidths: {
        ...prevState.columnWidths,
        [this.resizingColumn]: newWidth
      }
    }));
  };

  handleResizeEnd = () => {
    this.resizingColumn = null;
    document.removeEventListener('mousemove', this.handleResizeMove);
    document.removeEventListener('mouseup', this.handleResizeEnd);
    this.saveState();
  };

  renderSortIcon = (column) => {
    const { sortColumn, sortDirection } = this.state;
    if (sortColumn !== column) {
      return <i className="fa fa-sort" style={{ marginLeft: '8px', color: 'var(--text-muted)', opacity: 0.3 }} />;
    }
    return <i className={`fa ${sortDirection === 'asc' ? 'fa-sort-alpha-asc' : 'fa-sort-alpha-desc'}`} style={{ marginLeft: '8px', color: 'var(--text-color)' }} />;
  }

  toggleColumn = (colKey) => {
    this.setState(prevState => {
      const visibleColumns = [...prevState.visibleColumns];
      const index = visibleColumns.indexOf(colKey);
      if (index > -1) {
        visibleColumns.splice(index, 1);
      } else {
        visibleColumns.push(colKey);
      }
      return { visibleColumns };
    }, this.saveState);
  }

  toggleSelection = (userId) => {
    this.setState(prevState => {
      const selectedUsers = [...prevState.selectedUsers];
      const index = selectedUsers.indexOf(userId);
      if (index === -1) {
        selectedUsers.push(userId);
      } else {
        selectedUsers.splice(index, 1);
      }
      return { selectedUsers };
    });
  }

  toggleAllSelection = (visibleUserIds) => {
    this.setState(prevState => {
      const allSelected = visibleUserIds.every(id => prevState.selectedUsers.includes(id));
      if (allSelected) {
        // Deselect all visible
        return { selectedUsers: prevState.selectedUsers.filter(id => !visibleUserIds.includes(id)) };
      } else {
        // Select all visible
        const newSelection = new Set([...prevState.selectedUsers, ...visibleUserIds]);
        return { selectedUsers: Array.from(newSelection) };
      }
    });
  }

  exportToCSV = () => {
    const { items, filter, intl: { formatMessage } } = this.props;
    const { selectedUsers } = this.state;
    
    if (selectedUsers.length === 0) {
      alert("Bitte wählen Sie zuerst die Kontakte aus, die Sie exportieren möchten.");
      return;
    }

    const usersToExport = items.filter(u => selectedUsers.includes(u.id));
    
    let csvContent = "data:text/csv;charset=utf-8,";
    // Header
    csvContent += ["ID", "Name", "E-Mail", "Telefon", "Unternehmen", "Erstellt", "Letzte Anmeldung", "Rolle"].join(";") + "\r\n";
    
    // Rows
    usersToExport.forEach(user => {
      const role = user.is_admin ? formatMessage(messages.ADMIN) : formatMessage(messages.USER);
      const row = [
        user.id,
        user.name ? `"${user.name.replace(/"/g, '""')}"` : "",
        user.email ? `"${user.email.replace(/"/g, '""')}"` : "",
        user.phone ? `"${user.phone.replace(/"/g, '""')}"` : "",
        user.company_names ? `"${user.company_names.replace(/"/g, '""')}"` : "",
        user.created ? moment(user.created, 'YYYY-MM-DD HH:mm:ss').format('DD.MM.YYYY HH:mm') : "",
        user.last_login ? moment(user.last_login, 'YYYY-MM-DD HH:mm:ss').format('DD.MM.YYYY HH:mm') : "",
        `"${role}"`
      ];
      csvContent += row.join(";") + "\r\n";
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `telelook_kontakte_export_${moment().format('YYYYMMDD_HHmm')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  render() {
    const { items, filter, intl: { formatMessage } } = this.props;
    
    let filteredItems = items || [];
    if (filter) {
      const lowerFilter = filter.toLowerCase();
      filteredItems = filteredItems.filter(user => {
        return (user.name && user.name.toLowerCase().includes(lowerFilter)) ||
               (user.email && user.email.toLowerCase().includes(lowerFilter)) ||
               (user.phone && user.phone.toLowerCase().includes(lowerFilter));
      });
    }

    if (!(filteredItems && filteredItems.length)) {
      return <p style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>{formatMessage(messages.NO_USERS_FOUND)}</p>;
    }

    const { sortColumn, sortDirection, columnWidths } = this.state;
    const sortedItems = [...filteredItems].sort((a, b) => {
      if (!sortColumn) return 0;
      let valA = a[sortColumn];
      let valB = b[sortColumn];
      
      if (typeof valA === 'string') valA = valA.toLowerCase();
      if (typeof valB === 'string') valB = valB.toLowerCase();
      
      if (valA === valB) return 0;
      if (valA === undefined || valA === null) return sortDirection === 'asc' ? 1 : -1;
      if (valB === undefined || valB === null) return sortDirection === 'asc' ? -1 : 1;
      
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      return sortDirection === 'asc' ? 1 : -1;
    });

    const thBaseStyle = {
      padding: '16px 24px',
      color: 'var(--text-muted)',
      fontWeight: 'bold',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      position: 'relative',
      cursor: 'pointer',
      userSelect: 'none'
    };

    const getThStyle = (key) => ({
      ...thBaseStyle,
      width: `${columnWidths[key]}px`,
      minWidth: `${columnWidths[key]}px`,
      maxWidth: `${columnWidths[key]}px`,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    });

    const resizerStyle = {
      position: 'absolute',
      right: 0,
      top: 0,
      bottom: 0,
      width: '6px',
      cursor: 'col-resize',
      backgroundColor: 'transparent',
      zIndex: 1,
    };

    const renderResizer = (key) => (
      <div 
        style={resizerStyle} 
        onMouseDown={(e) => this.handleResizeStart(e, key)}
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--text-muted)'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
      />
    );

    return (
      <div style={{ width: '100%' }}>
        <style>
          {`
            .responsive-users-table {
              width: 100%;
              table-layout: fixed;
              border-collapse: collapse;
              text-align: left;
              font-size: 14px;
            }
            @media (max-width: 1100px) {
              .responsive-users-table, 
              .responsive-users-table thead, 
              .responsive-users-table tbody, 
              .responsive-users-table th, 
              .responsive-users-table td, 
              .responsive-users-table tr { 
                display: block; 
              }
              .responsive-users-table thead tr { 
                position: absolute;
                top: -9999px;
                left: -9999px;
              }
              .responsive-users-table tr { 
                border: 1px solid var(--panel-border);
                margin-bottom: 16px;
                border-radius: 8px;
                background-color: var(--panel-bg);
                padding: 8px 0;
              }
              .responsive-users-table td { 
                border: none;
                border-bottom: 1px solid var(--panel-border); 
                position: relative;
                padding-left: 50% !important; 
                white-space: normal !important;
                text-align: left !important;
                display: flex !important;
                justify-content: flex-start;
                align-items: center;
              }
              .responsive-users-table td:last-child {
                border-bottom: 0;
              }
              .responsive-users-table td:before { 
                position: absolute;
                top: 50%;
                left: 16px;
                transform: translateY(-50%);
                width: 45%; 
                padding-right: 10px; 
                white-space: nowrap;
                font-weight: bold;
                color: var(--text-muted);
                text-transform: uppercase;
                font-size: 11px;
                content: attr(data-label);
              }
            }
          `}
        </style>
        
        {this.state.selectedUsers.length > 0 && (
          <div style={{ marginBottom: '16px', padding: '12px 16px', backgroundColor: 'rgba(59, 130, 246, 0.1)', border: '1px solid #3b82f6', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#1e40af', fontWeight: 'bold' }}>{this.state.selectedUsers.length} Kontakt(e) ausgewählt</span>
            <button 
              onClick={this.exportToCSV}
              style={{ backgroundColor: '#3b82f6', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <i className="fa fa-download" /> {formatMessage(messages.EXPORT_CSV)}
            </button>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px', position: 'relative' }}>
          <button 
            onClick={() => this.setState(prev => ({ showColumnPicker: !prev.showColumnPicker }))}
            style={{ backgroundColor: 'var(--panel-bg)', color: 'var(--text-color)', border: '1px solid var(--panel-border)', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <i className="fa fa-columns" /> {formatMessage(messages.CUSTOMIZE_COLUMNS)}
          </button>
          
          {this.state.showColumnPicker && (
            <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '8px', backgroundColor: 'var(--panel-bg)', border: '1px solid var(--panel-border)', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', zIndex: 100, width: '250px', padding: '12px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '12px', color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase' }}>Spalten einblenden</div>
              {[
                { key: 'colUser', label: formatMessage(messages.NAME) + ' & ' + formatMessage(messages.EMAIL) },
                { key: 'col2', label: formatMessage(messages.PHONE) },
                { key: 'colCompany', label: formatMessage(messages.COMPANY) },
                { key: 'colActivity', label: formatMessage(messages.ACTIVITY) },
                { key: 'colRole', label: formatMessage(messages.ROLE) },
                { key: 'col5', label: formatMessage(messages.COUNT_COMPANIES) },
              ].map(col => (
                <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0', cursor: 'pointer', borderBottom: '1px solid var(--panel-border)' }}>
                  <input 
                    type="checkbox" 
                    checked={this.state.visibleColumns.includes(col.key)} 
                    onChange={() => this.toggleColumn(col.key)} 
                    style={{ cursor: 'pointer' }}
                  />
                  {col.label}
                </label>
              ))}
            </div>
          )}
        </div>

        <table className="responsive-users-table">
          <thead>
            <tr style={{ backgroundColor: 'var(--panel-bg)', borderBottom: '1px solid var(--panel-border)' }}>
              <th style={{ ...getThStyle('colSelect'), textAlign: 'center' }}>
                <input 
                  type="checkbox" 
                  onChange={() => this.toggleAllSelection(sortedItems.map(u => u.id))} 
                  checked={sortedItems.length > 0 && sortedItems.every(u => this.state.selectedUsers.includes(u.id))}
                  style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                />
                {renderResizer('colSelect')}
              </th>
              {this.state.visibleColumns.includes('colUser') && (
                <th onClick={() => this.toggleSort('name')} style={getThStyle('colUser')}>
                  {formatMessage(messages.NAME)}
                  {this.renderSortIcon('name')}
                  {renderResizer('colUser')}
                </th>
              )}
              {this.state.visibleColumns.includes('col2') && (
                <th onClick={() => this.toggleSort('phone')} style={getThStyle('col2')}>
                  {formatMessage(messages.PHONE)}
                  {this.renderSortIcon('phone')}
                  {renderResizer('col2')}
                </th>
              )}
              {this.state.visibleColumns.includes('colCompany') && (
                <th onClick={() => this.toggleSort('company_names')} style={getThStyle('colCompany')}>
                  {formatMessage(messages.COMPANY)}
                  {this.renderSortIcon('company_names')}
                  {renderResizer('colCompany')}
                </th>
              )}
              {this.state.visibleColumns.includes('colActivity') && (
                <th onClick={() => this.toggleSort('created')} style={getThStyle('colActivity')}>
                  {formatMessage(messages.ACTIVITY)}
                  {this.renderSortIcon('created')}
                  {renderResizer('colActivity')}
                </th>
              )}
              {this.state.visibleColumns.includes('colRole') && (
                <th onClick={() => this.toggleSort('is_admin')} style={getThStyle('colRole')}>
                  {formatMessage(messages.ROLE)}
                  {this.renderSortIcon('is_admin')}
                  {renderResizer('colRole')}
                </th>
              )}
              {this.state.visibleColumns.includes('col5') && (
                <th onClick={() => this.toggleSort('company_count')} style={{ ...getThStyle('col5'), textAlign: 'center' }}>
                  {formatMessage(messages.COUNT_COMPANIES)}
                  {this.renderSortIcon('company_count')}
                  {renderResizer('col5')}
                </th>
              )}
              <th style={{ ...thBaseStyle, padding: '16px 24px', width: 'auto', cursor: 'default' }} />
            </tr>
          </thead>
          <tbody>
            {sortedItems.map(user => (
              <tr key={user.id} style={{ borderBottom: '1px solid var(--panel-border)', transition: 'background-color 0.2s', backgroundColor: this.state.selectedUsers.includes(user.id) ? 'rgba(59, 130, 246, 0.05)' : 'transparent', ':hover': { backgroundColor: 'var(--btn-default-hover-bg)' } }}>
              <td data-label="Auswahl" style={{ padding: '16px 24px', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <input 
                  type="checkbox" 
                  checked={this.state.selectedUsers.includes(user.id)} 
                  onChange={() => this.toggleSelection(user.id)} 
                  style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                />
              </td>
              {this.state.visibleColumns.includes('colUser') && (
                <td data-label={formatMessage(messages.NAME)} style={{ padding: '16px 24px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', overflow: 'hidden' }}>
                    <div style={{ width: '40px', height: '40px', minWidth: '40px', borderRadius: '8px', backgroundColor: 'var(--pill-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
                      <i className="fa fa-user" style={{ fontSize: '16px' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', gap: '4px' }}>
                      <TextOverflow title={user.name}>
                        <span style={{ fontWeight: 'bold', color: 'var(--text-color)' }}>
                          {filter ? <Highlight search={filter}>{user.name}</Highlight> : user.name}
                        </span>
                      </TextOverflow>
                      <TextOverflow title={user.email}>
                        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                          {filter ? <Highlight search={filter}>{user.email}</Highlight> : user.email}
                        </span>
                      </TextOverflow>
                    </div>
                  </div>
                </td>
              )}
              {this.state.visibleColumns.includes('col2') && (
                <td data-label={formatMessage(messages.PHONE)} style={{ padding: '16px 24px', color: 'var(--text-color)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {filter ? <Highlight search={filter}>{user.phone || ''}</Highlight> : user.phone}
                </td>
              )}
              {this.state.visibleColumns.includes('colCompany') && (
                <td data-label={formatMessage(messages.COMPANY)} style={{ padding: '16px 24px', color: 'var(--text-color)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <TextOverflow title={user.company_names || '-'}>
                    {filter ? <Highlight search={filter}>{user.company_names || '-'}</Highlight> : (user.company_names || '-')}
                  </TextOverflow>
                </td>
              )}
              {this.state.visibleColumns.includes('colActivity') && (
                <td data-label={formatMessage(messages.ACTIVITY)} style={{ padding: '16px 24px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px' }}>
                    <div style={{ color: 'var(--text-color)' }} title="Zuletzt online">
                      <i className="fa fa-sign-in" style={{ color: 'var(--text-muted)', width: '16px' }} /> 
                      {user.last_login ? moment(user.last_login, 'YYYY-MM-DD HH:mm:ss').fromNow() : '-'}
                    </div>
                    <div style={{ color: 'var(--text-muted)' }} title="Hinzugefügt am">
                      <i className="fa fa-calendar" style={{ width: '16px' }} /> 
                      {user.created ? moment(user.created, 'YYYY-MM-DD HH:mm:ss').format('DD.MM.YY') : '-'}
                    </div>
                  </div>
                </td>
              )}
              {this.state.visibleColumns.includes('colRole') && (
                <td data-label={formatMessage(messages.ROLE)} style={{ padding: '16px 24px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user.is_admin ? (
                      <span style={{ display: 'inline-block', padding: '4px 8px', borderRadius: '4px', backgroundColor: '#06b6d4', color: '#fff', fontSize: '11px', fontWeight: 'bold' }}>
                        {formatMessage(messages.ADMIN)}
                      </span>
                    ) : (
                      <span style={{ display: 'inline-block', padding: '4px 8px', borderRadius: '4px', backgroundColor: '#94a3b8', color: '#fff', fontSize: '11px', fontWeight: 'bold' }}>
                        {formatMessage(messages.USER)}
                      </span>
                    )}
                  </td>
              )}
              {this.state.visibleColumns.includes('col5') && (
                <td data-label={formatMessage(messages.COUNT_COMPANIES)} style={{ padding: '16px 24px', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: '9999px', backgroundColor: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6', fontWeight: 'bold' }}>
                    {user.company_count}
                  </span>
                </td>
              )}
              <td data-label="Status" style={{ padding: '16px 24px', display: 'flex', gap: '6px', justifyContent: 'flex-start', flexWrap: 'wrap' }}>
                  {user.is_inactive ? (
                    <span style={{ display: 'inline-block', padding: '4px 8px', borderRadius: '4px', backgroundColor: '#ef4444', color: '#fff', fontSize: '11px', fontWeight: 'bold' }}>
                      {formatMessage(messages.DISABLED)}
                    </span>
                  ) : null}
                  {user.is_deleted && (
                    <span style={{ display: 'inline-block', padding: '4px 8px', borderRadius: '4px', backgroundColor: '#ef4444', color: '#fff', fontSize: '11px', fontWeight: 'bold' }}>
                      {formatMessage(messages.DELETED)}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
}

export default injectIntl(AllUsers);

